import { IApplicationSection, IPermissionAction } from './permissions';

export interface ISummaryLine {
  collection: string;
  label: string;
  actionsText: string;
}

export interface IRoleSummary {
  lines: ISummaryLine[];
  cannotNote: string | null;
  includedBlurb: string;
  selectedCount: number;
  unrecognised: string[];
}

const joinWith = (items: string[], conjunction: string): string => {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} ${conjunction} ${items.at(-1)}`;
};

const capitaliseFirst = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

const selectedLabels = (actions: IPermissionAction[], selectedSet: Set<string>): string[] =>
  actions
    .filter((action) => !action.disabled && selectedSet.has(action.slug))
    .map((action) => action.label.toLowerCase());

const lineFor = (collection: string, label: string, labels: string[]): ISummaryLine | null =>
  labels.length > 0 ? { collection, label, actionsText: joinWith(labels, 'and') } : null;

export const deriveSummary = (
  sections: IApplicationSection[],
  selected: string[],
): IRoleSummary => {
  const selectedSet = new Set(selected);
  const groups = sections.flatMap((section) => section.groups);

  const lines = groups.flatMap((group) => {
    const ownLabels = selectedLabels(group.actions, selectedSet);
    const parentLabels =
      ownLabels.length > 0 && group.readSlug === null ? ['view', ...ownLabels] : ownLabels;

    const parentLine = lineFor(group.collection, group.label, parentLabels);
    const childLines = group.children
      .map((child) =>
        lineFor(child.collection, child.label, selectedLabels(child.actions, selectedSet)),
      )
      .filter((line): line is ISummaryLine => line !== null);

    return parentLine ? [parentLine, ...childLines] : childLines;
  });

  const anyDeleteSelected = groups.some((group) =>
    [...group.actions, ...group.children.flatMap((child) => child.actions)].some(
      (action) => !action.disabled && selectedSet.has(action.slug) && action.action === 'delete',
    ),
  );

  const unseenLabels = groups
    .filter((group) => group.readSlug !== null && !selectedSet.has(group.readSlug))
    .map((group) => group.label.toLowerCase());

  const noteParts = [
    ...(!anyDeleteSelected ? ['cannot delete anything'] : []),
    ...(unseenLabels.length > 0 ? [`cannot see ${joinWith(unseenLabels, 'or')}`] : []),
  ];

  const cannotNote =
    groups.length > 0 && selected.length > 0 && noteParts.length > 0
      ? `${capitaliseFirst(noteParts.join(', and '))}.`
      : null;

  const impliedLabels = groups
    .filter((group) => group.readSlug === null)
    .map((group) => group.label.toLowerCase());

  const includedBlurb =
    impliedLabels.length > 0 ? `Viewing ${joinWith(impliedLabels, 'and')}.` : '';

  const cataloguedSlugs = new Set(
    groups.flatMap((group) =>
      [...group.actions, ...group.children.flatMap((child) => child.actions)]
        .filter((action) => !action.disabled)
        .map((action) => action.slug),
    ),
  );
  const unrecognised = selected
    .filter((slug) => !cataloguedSlugs.has(slug))
    .toSorted((a, b) => a.localeCompare(b));

  return {
    lines,
    cannotNote,
    includedBlurb,
    selectedCount: selected.length,
    unrecognised,
  };
};
